<template>
  <div>
    <div v-if="!universitiesExist">
      <div class="relative">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-start">
          <span class="pr-3 bg-light-grey text-lg font-medium text-vice-teal-700">University</span>
        </div>
      </div>
      <div class="mt-8 text-gray-500 text-center">University with students will appear here.</div>
    </div>

    <div v-for="uni in this.universities" :key="uni.id" class="pb-11">
      <div class="relative">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-between items-center">
          <span class="pr-3 bg-light-grey text-lg font-medium text-vice-teal-700">
            Accounts at {{ uni.name }}
          </span>
          <div class="bg-light-grey pl-3 flex items-center gap-3">
            <div class="flex items-center gap-1">
              <ViceButton medium dark @click="openImportModal(uni.id)">Import</ViceButton>
            </div>
            <select
              @change="filterByRole($event.target.value, uni.id)"
              class="
                block
                pl-3
                pr-10
                py-2
                text-base
                border-gray-300
                focus:outline-none
                focus:ring-indigo-500
                focus:border-indigo-500
                sm:text-sm
                rounded-md
              "
            >
              <!-- :class="{ 'opacity-50 pointer-events-none': !Object.keys(uni.users).length }"-->

              <option
                v-for="role in roles"
                :value="role"
                :key="uni.id + role"
                :selected="roles[0] === role"
              >
                {{ role }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="flex gap-2 mt-6">
        <button
          type="button"
          class="px-4 py-2 rounded-md text-sm font-medium"
          :class="
            accountTabFor(uni.id) === 'people'
              ? 'bg-white shadow-sm text-vice-teal-900'
              : 'bg-transparent text-gray-500 hover:text-gray-700'
          "
          @click="setAccountTab(uni.id, 'people')"
        >
          People ({{ totalAccountCount(uni.id) }})
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded-md text-sm font-medium"
          :class="
            accountTabFor(uni.id) === 'groups'
              ? 'bg-white shadow-sm text-vice-teal-900'
              : 'bg-transparent text-gray-500 hover:text-gray-700'
          "
          @click="setAccountTab(uni.id, 'groups')"
        >
          Groups ({{ (uni.groups || []).length }})
        </button>
      </div>

      <div v-if="accountTabFor(uni.id) === 'groups'" class="mt-3">
        <div class="flex items-center justify-between">
          <p class="text-sm text-gray-500">
            Groups let you organise learners into cohorts and invite them to sessions in one click.
          </p>
          <ViceButton medium dark icon="PlusIcon" @click="openNewGroupModal(uni.id)">
            New group
          </ViceButton>
        </div>

        <div v-if="!(uni.groups || []).length" class="mt-8 text-gray-500 text-center">
          Groups at {{ uni.name }} will appear here.
        </div>
        <div v-else class="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="group in sortedGroups(uni)"
            :key="group.id"
            class="border border-gray-200 rounded-lg p-4"
          >
            <div class="flex items-start justify-between">
              <div>
                <p class="font-medium text-vice-teal-900">{{ group.name }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  {{ group.users_count }} student{{ group.users_count === 1 ? '' : 's' }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <PencilEdit class="cursor-pointer" @click="openEditGroupModal(uni.id, group)" />
                <TrashRemove class="cursor-pointer" @click="confirmDeleteGroup(uni.id, group)" />
              </div>
            </div>
            <button
              type="button"
              class="mt-4 text-sm text-vice-teal-600 hover:text-vice-teal-700 font-medium"
              @click="viewGroupMembers(uni.id, group.id)"
            >
              View members &rarr;
            </button>
          </div>
        </div>
      </div>

      <div v-else class="mt-3">
        <div class="flex items-center gap-3">
          <div class="relative flex-1 max-w-sm">
            <SearchIcon class="w-4 h-4 text-gray-400 absolute left-3 top-1/4 -translate-y-1/2" />
            <input
              type="text"
              :value="searchQuery[uni.id] ?? ''"
              @input="setSearchQuery(uni.id, $event.target.value)"
              placeholder="Search by name, email or ID..."
              class="
                pl-9
                pr-3
                py-2
                w-full
                text-sm
                border-gray-300
                focus:outline-none
                focus:ring-indigo-500
                focus:border-indigo-500
                rounded-md
              "
            />
          </div>
          <select
            :value="groupFilterId[uni.id] ?? ''"
            @change="setGroupFilter(uni.id, $event.target.value)"
            class="
              block
              pl-3
              pr-10
              py-2
              text-sm
              border-gray-300
              focus:outline-none
              focus:ring-indigo-500
              focus:border-indigo-500
              rounded-md
            "
          >
            <option value="">All groups</option>
            <option v-for="group in sortedGroups(uni)" :key="group.id" :value="group.id">
              {{ group.name }}
            </option>
          </select>
        </div>

        <div v-if="!Object.keys(uni.users).length" class="mt-8 text-gray-500 text-center">
          <span class="capitalize">{{ selectedRoleByUni[uni.id] }}s</span>
          at {{ uni.name }} will appear here.
        </div>
        <div v-else-if="!visibleUsers(uni).length" class="mt-8 text-gray-500 text-center">
          No accounts match your search.
        </div>
        <div v-else class="my-1 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div class="shadow-sm overflow-hidden border border-gray-200 rounded-lg">
              <table class="table-fixed min-w-full divide-y divide-gray-50">
                <thead>
                  <tr class="border-b border-gray-200">
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xxs font-medium text-gray-500 tracking-wider"
                    >
                      NAME
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xxs font-medium text-gray-500 tracking-wider"
                    >
                      EMAIL
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xxs font-medium text-gray-500 tracking-wider"
                    >
                      UNIVERSITY
                    </th>
                    <th
                      scope="col"
                      class="
                        px-6
                        py-3
                        text-center text-xxs
                        font-medium
                        text-gray-500
                        tracking-wider
                      "
                    >
                      GROUP
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-xxs font-medium text-gray-500 tracking-wider"
                    >
                      PRO-COURSE
                    </th>
                    <th
                      scope="col"
                      class="
                        px-6
                        py-3
                        text-center text-xxs
                        font-medium
                        text-gray-500
                        tracking-wider
                      "
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      class="
                        px-6
                        py-3
                        text-center text-xxs
                        font-medium
                        text-gray-500
                        tracking-wider
                      "
                    >
                      AVATAR
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(user, userIdx) in visibleUsers(uni)"
                    :key="user.id"
                    :class="userIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  >
                    <td class="pl-4 pr-2 py-4 text-xs font-medium text-vice-teal-900">
                      <span class="cursor-pointer">
                        {{ user.name }}
                      </span>
                    </td>
                    <td class="px-2 py-4 text-left whitespace-nowrap text-xs text-gray-500">
                      {{ user.email }}
                    </td>
                    <td class="px-2 py-4 whitespace-nowrap text-xs text-gray-500">
                      {{ uni.name }}
                    </td>
                    <td class="px-2 py-4 whitespace-nowrap text-xs text-gray-500 text-center">
                      <span
                        v-if="user.group_name"
                        class="
                          inline-block
                          px-2
                          py-0.5
                          rounded-full
                          bg-vice-teal-150
                          text-vice-teal-900 text-xxs
                          font-medium
                        "
                      >
                        {{ user.group_name }}
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="px-2 py-4 text-center">
                      <SquaredSuccessIcon
                        v-if="user.pro_course"
                        class="inline-block"
                      ></SquaredSuccessIcon>
                      <SquaredFailIcon v-else class="inline-block"></SquaredFailIcon>
                    </td>
                    <td class="px-2 py-4 text-center whitespace-nowrap text-xs text-gray-500">
                      {{ user.id }}
                    </td>
                    <td class="px-2 py-4 text-center whitespace-nowrap text-xs text-gray-500">
                      <ViceAvatar
                        :image="user.profile_photo_url"
                        :name="user.name"
                        class="w-8 h-8 inline-block"
                      />
                    </td>
                    <td class="pl-2 pr-4 py-4 text-center whitespace-nowrap text-xs text-gray-500">
                      <div v-if="user.role === 'student'">
                        <div
                          v-if="user.request_id"
                          class="flex justify-evenly gap-3"
                          :key="user.request_id"
                        >
                          <ViceButton
                            type="button"
                            @click="denyJoinRequest(user.request_id, user.id)"
                            :disabled="this.denyPending"
                            medium
                            rose
                          >
                            DENY
                          </ViceButton>
                          <ViceButton
                            type="button"
                            @click="acceptJoinRequest(user)"
                            :disabled="this.acceptPending"
                            medium
                            dark
                          >
                            ACCEPT
                          </ViceButton>
                        </div>
                        <div v-else class="inline-flex sm:items-center gap-10" :key="user.id">
                          <PencilEdit
                            class="cursor-pointer"
                            @click="toggleEditUserModal(true, user)"
                          />
                          <TrashRemove
                            class="cursor-pointer"
                            @click="removeUserFromUniversity(user.id, user.university_id)"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <EditUserModal
    @toggleEditUserModal="toggleEditUserModal"
    v-if="isEditUser"
    :user="editableUser"
  />
  <ImportStudentsModal
    v-if="isImportOpen"
    :universities="initialUnis"
    :preselectedUniversityId="importUniversityId"
    @close="isImportOpen = false"
    @imported="onStudentsImported"
  />
  <GroupFormModal
    v-if="isGroupModalOpen"
    :universityId="groupModalUniversityId"
    :group="editingGroup"
    :students="studentsForModal(groupModalUniversityId)"
    @close="closeGroupModal"
    @saved="onGroupSaved"
  />
  <jet-confirmation-modal :show="!!groupBeingDeleted" @close="groupBeingDeleted = null">
    <template #title>Delete group</template>

    <template #content>
      Are you sure you would like to delete "{{ groupBeingDeleted?.group?.name }}"? Students in this
      group will not be removed from the university, but they will no longer belong to a group.
    </template>

    <template #footer>
      <jet-secondary-button @click="groupBeingDeleted = null">Cancel</jet-secondary-button>

      <jet-danger-button
        class="ml-2"
        @click="deleteGroup"
        :class="{ 'opacity-25': deletingGroup }"
        :disabled="deletingGroup"
      >
        Delete
      </jet-danger-button>
    </template>
  </jet-confirmation-modal>
</template>

<script>
import { Inertia } from '@inertiajs/inertia';
import axios from 'axios';
import { SearchIcon } from '@heroicons/vue/solid';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import SquaredSuccessIcon from '@/Components/Icons/SquaredSuccessIcon.vue';
import SquaredFailIcon from '@/Components/Icons/SquaredFailIcon.vue';
import PencilEdit from '@/Components/Icons/PencilEdit.vue';
import TrashRemove from '@/Components/Icons/TrashRemove.vue';
import EditUserModal from '@/Components/Modals/EditUserModal.vue';
import ImportStudentsModal from '@/Components/Modals/ImportStudentsModal.vue';
import GroupFormModal from '@/Components/Modals/GroupFormModal.vue';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetDangerButton from '@/Jetstream/DangerButton.vue';

export default {
  name: 'Students',
  components: {
    EditUserModal,
    ImportStudentsModal,
    GroupFormModal,
    JetConfirmationModal,
    JetSecondaryButton,
    JetDangerButton,
    TrashRemove,
    PencilEdit,
    ViceButton,
    SquaredFailIcon,
    SquaredSuccessIcon,
    ViceAvatar,
    SearchIcon,
  },
  computed: {
    Inertia() {
      return Inertia;
    },
    universitiesExist() {
      return Object.keys(this.$page.props.universities).length;
    },
  },
  data() {
    return {
      universities: [],
      initialUnis: [],
      isEditUser: false,
      editableUser: null,
      denyPending: false,
      acceptPending: false,
      roles: {
        student: 'Students',
        facilitator: 'Facilitators',
        clinician: 'Clinicians',
        admin: 'Admins',
      },
      selectedRoleByUni: {},
      isImportOpen: false,
      importUniversityId: null,
      accountTab: {},
      searchQuery: {},
      groupFilterId: {},
      isGroupModalOpen: false,
      groupModalUniversityId: null,
      editingGroup: null,
      groupBeingDeleted: null,
      deletingGroup: false,
    };
  },

  mounted() {
    this.setUniversities();
    this.filterByRole();
  },

  methods: {
    setUniversities() {
      this.universities = this.$page.props.universities
        .map((university) => {
          const joinRequests = university.join_university_requests.concat(university.users);
          const joinClinicians = joinRequests.concat(university.clinicians);

          return {
            id: university.id,
            name: university.name,
            groups: university.groups ?? [],
            users: joinClinicians,
            userCount: joinClinicians.length,
          };
        })
        .sort((a, b) => b.userCount - a.userCount);
      this.initialUnis = this.universities;
      this.universities.forEach((uni) => {
        this.selectedRoleByUni[uni.id] = 'student';
      });
    },

    filterByRole(roleName = null, uniId = null) {
      if (uniId === null) {
        this.universities = this.universities.map((uni) => {
          const filteredUsers = uni.users.filter((user) => user.role === 'student');
          return {
            ...uni,
            users: filteredUsers,
            userCount: filteredUsers.length,
          };
        });
      } else {
        const role = Object.keys(this.roles).find((key) => this.roles[key] === roleName);
        this.selectedRoleByUni[uniId] = role;
        const originalUni = this.initialUnis.find((uni) => uni.id === uniId);
        this.universities = this.universities.map((uni) => {
          if (uniId === uni.id) {
            const filteredUsers = originalUni.users.filter((user) => user.role === role);
            return {
              ...uni,
              users: filteredUsers,
              userCount: filteredUsers.length,
            };
          }
          return { ...uni };
        });
      }
    },

    addImportedStudents(uniId, createdUsers) {
      if (!createdUsers?.length) return;

      const originalUni = this.initialUnis.find((uni) => uni.id === uniId);
      if (originalUni) {
        originalUni.users = originalUni.users.concat(createdUsers);
        originalUni.userCount = originalUni.users.length;
      }

      const currentRole = this.selectedRoleByUni[uniId] ?? 'student';
      this.filterByRole(this.roles[currentRole], uniId);
    },

    async acceptJoinRequest(user) {
      this.acceptPending = true;
      try {
        const accepted = await axios.post(route('user.join.university.accept', user.request_id));
        if (accepted.data) {
          this.markAsJoined(user.id);
          this.$page.props.message = 'User join request was ACCEPTED';
        }
      } catch (error) {
        console.error(error);
      }
      this.acceptPending = false;
    },

    async denyJoinRequest(requestId, userId) {
      this.denyPending = true;
      try {
        const deny = await axios.post(route('user.join.university.deny', requestId));
        if (deny.data) {
          this.removeUserFromList(userId);
          this.$page.props.message = 'User join request was DENIED';
        }
      } catch (error) {
        console.error(error);
      }
      this.denyPending = false;
    },

    markAsJoined(userId) {
      this.universities.forEach((university) => {
        const user = university.users.find((item) => item.id === userId);
        if (user) {
          delete user.request_id;
        }
      });
    },

    removeUserFromList(userId) {
      this.universities.forEach((university) => {
        university.users = university.users.filter((user) => user.id !== userId);
      });
      this.initialUnis.forEach((university) => {
        university.users = university.users.filter((user) => user.id !== userId);
      });
    },

    updateUserInList(user) {
      const previousGroupId =
        this.universities
          .flatMap((university) => university.users)
          .find((item) => item.id === user.id)?.group_id ?? null;
      const newGroupId = user.group_id ?? null;

      [this.universities, this.initialUnis].forEach((list) => {
        list.forEach((university) => {
          const student = university.users.find((item) => item.id === user.id);
          if (student) {
            Object.assign(student, user);
          }
        });
      });

      if (previousGroupId !== newGroupId) {
        this.universities.forEach((university) => {
          (university.groups || []).forEach((group) => {
            if (group.id === previousGroupId) {
              group.users_count = Math.max(0, group.users_count - 1);
            } else if (group.id === newGroupId) {
              group.users_count += 1;
            }
          });
        });
      }
    },

    async toggleEditUserModal(show = false, user = null) {
      this.isEditUser = show;

      if (show && user) {
        this.editableUser = user;
      } else if (!show && user) {
        this.updateUserInList(user);
        this.$page.props.message = `Student ${user.name} has been updated`;
      } else {
        this.editableUser = null;
      }
    },

    async removeUserFromUniversity(userId, universityId) {
      try {
        const remove = await axios.post(route('university.remove.user', universityId), { userId });
        if (remove.data) {
          this.removeUserFromList(userId);
          this.$page.props.message = 'User was removed from the university';
        } else {
          this.$page.props.error = remove.data.error;
        }
      } catch (error) {
        console.error(error);
      }
    },

    openImportModal(universityId) {
      this.importUniversityId = universityId;
      this.isImportOpen = true;
    },

    onStudentsImported(result) {
      this.isImportOpen = false;
      this.addImportedStudents(this.importUniversityId, result?.created);
    },

    sortedGroups(uni) {
      return [...(uni.groups || [])].sort((a, b) => a.id - b.id);
    },

    accountTabFor(uniId) {
      return this.accountTab[uniId] ?? 'people';
    },

    setAccountTab(uniId, tab) {
      this.accountTab[uniId] = tab;
    },

    totalAccountCount(uniId) {
      const originalUni = this.initialUnis.find((uni) => uni.id === uniId);
      return originalUni ? originalUni.users.length : 0;
    },

    setSearchQuery(uniId, value) {
      this.searchQuery[uniId] = value;
    },

    setGroupFilter(uniId, value) {
      this.groupFilterId[uniId] = value ? Number(value) : null;
    },

    visibleUsers(uni) {
      const query = (this.searchQuery[uni.id] ?? '').trim().toLowerCase();
      const groupId = this.groupFilterId[uni.id] ?? null;

      return uni.users.filter((user) => {
        if (groupId && user.group_id !== groupId) return false;
        if (!query) return true;

        return (
          user.name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          String(user.id).includes(query)
        );
      });
    },

    studentsForModal(uniId) {
      const originalUni = this.initialUnis.find((uni) => uni.id === uniId);
      if (!originalUni) return [];
      return originalUni.users.filter((user) => user.role === 'student' && !user.request_id);
    },

    openNewGroupModal(uniId) {
      this.groupModalUniversityId = uniId;
      this.editingGroup = null;
      this.isGroupModalOpen = true;
    },

    openEditGroupModal(uniId, group) {
      this.groupModalUniversityId = uniId;
      this.editingGroup = group;
      this.isGroupModalOpen = true;
    },

    closeGroupModal() {
      this.isGroupModalOpen = false;
      this.editingGroup = null;
      this.groupModalUniversityId = null;
    },

    applyGroupUpdateToUsers(uni, memberIds, groupData) {
      if (!uni) return;
      uni.users.forEach((user) => {
        if (memberIds.has(user.id)) {
          user.group_id = groupData.id;
          user.group_name = groupData.name;
        } else if (user.group_id === groupData.id) {
          user.group_id = null;
          user.group_name = null;
        }
      });
    },

    onGroupSaved(groupData) {
      const uniId = this.groupModalUniversityId;
      const memberIds = new Set((groupData.users || []).map((user) => user.id));
      const summary = { id: groupData.id, name: groupData.name, users_count: memberIds.size };

      [this.universities, this.initialUnis].forEach((list) => {
        const uni = list.find((item) => item.id === uniId);
        if (!uni) return;

        // A student who joined this group may have moved out of another one -
        // work out how many members each other group lost before we mutate them.
        const otherGroupLosses = {};
        uni.users.forEach((user) => {
          if (memberIds.has(user.id) && user.group_id && user.group_id !== groupData.id) {
            otherGroupLosses[user.group_id] = (otherGroupLosses[user.group_id] || 0) + 1;
          }
        });

        this.applyGroupUpdateToUsers(uni, memberIds, groupData);

        const groups = (uni.groups || [])
          .filter((group) => group.id !== groupData.id)
          .map((group) =>
            otherGroupLosses[group.id]
              ? {
                  ...group,
                  users_count: Math.max(0, group.users_count - otherGroupLosses[group.id]),
                }
              : group,
          );
        groups.push(summary);
        uni.groups = groups;
      });

      this.$page.props.message = `Group "${groupData.name}" was saved`;
      this.closeGroupModal();
    },

    viewGroupMembers(uniId, groupId) {
      this.groupFilterId[uniId] = groupId;
      this.accountTab[uniId] = 'people';
    },

    confirmDeleteGroup(uniId, group) {
      this.groupBeingDeleted = { uniId, group };
    },

    async deleteGroup() {
      if (!this.groupBeingDeleted) return;
      const { uniId, group } = this.groupBeingDeleted;
      this.deletingGroup = true;

      try {
        await axios.post(route('group.destroy', group.id));

        [this.universities, this.initialUnis].forEach((list) => {
          const uni = list.find((item) => item.id === uniId);
          if (!uni) return;
          uni.groups = (uni.groups || []).filter((item) => item.id !== group.id);
          uni.users.forEach((user) => {
            if (user.group_id === group.id) {
              user.group_id = null;
              user.group_name = null;
            }
          });
        });

        if (this.groupFilterId[uniId] === group.id) {
          this.groupFilterId[uniId] = null;
        }

        this.$page.props.message = `Group "${group.name}" was deleted`;
      } catch (error) {
        console.error(error);
        this.$page.props.error =
          error.response?.data?.error ?? 'Something went wrong while deleting the group.';
      } finally {
        this.deletingGroup = false;
        this.groupBeingDeleted = null;
      }
    },
  },
};
</script>
<style scoped>
td {
  max-width: 210px;
  overflow: hidden;
}
</style>
